var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx } from "react/jsx-runtime";
import { cloneElement, createContext, memo, useCallback, useContext, useEffect, useState, } from "react";
import classNames from "classnames";
import { useId } from "../../utils/hooks/useId";
import { getErrorMessage, getLocalIconUrl, retrieveSVGElement } from "./utils/functions";
// styles
import "./icon.scss";
// use the optional prop interface to define the default props
const defaultProps = {
    "data-testid": "tecma-icon",
    size: "medium",
    filled: false,
    isLogo: false,
};
/**
 * Context for providing the base URL for components of type `Icon`.
 * @example
 * <IconURLContext.Provider value="/icons/url">
 *   <Icon {...props} iconName="myIcon" />
 * </IconURLContext.Provider>
 */
export const IconURLContext = createContext("");
const Icon = (_a) => {
    var { className, src, iconName, size, filled, isLogo } = _a, svgProps = __rest(_a, ["className", "src", "iconName", "size", "filled", "isLogo"]);
    const classList = classNames("tecma-icon", { [`${size}`]: size }, { isLogo }, className);
    let iconsURL = useContext(IconURLContext);
    if (src) {
        iconsURL = src;
    }
    const completeIconName = iconName + (filled ? "-filled" : "");
    const completeIconLink = iconsURL && completeIconName && `${iconsURL}/${completeIconName}.svg`;
    const [svgElement, setSVGElement] = useState();
    const hash = useId();
    const updateIcon = useCallback(() => __awaiter(void 0, void 0, void 0, function* () {
        if (completeIconLink) {
            try {
                const remoteSVG = yield retrieveSVGElement(completeIconLink, hash);
                setSVGElement(remoteSVG);
                return;
            }
            catch (error) {
                console.error(`Error while retrieving remote SVG for icon "${completeIconName}"`, getErrorMessage(error));
            }
        }
        try {
            const localIconUrl = yield getLocalIconUrl(completeIconName);
            const localSVG = yield retrieveSVGElement(localIconUrl, hash);
            setSVGElement(localSVG);
        }
        catch (error) {
            setSVGElement(null);
            console.error(`Error while retrieving local SVG for icon "${completeIconName}"`, getErrorMessage(error));
        }
    }), [completeIconLink, hash, completeIconName]);
    useEffect(() => {
        updateIcon();
    }, [iconsURL, completeIconName, updateIcon]);
    return svgElement ? (cloneElement(svgElement, Object.assign(Object.assign({}, svgProps), { className: classList }))) : (_jsx("svg", { className: classList, "aria-label": svgProps["aria-label"] }));
};
Icon.defaultProps = defaultProps;
/**
 * Component for rendering an SVG in the virtual DOM given a URL and icon name.
 * For the icon's base URL can be either used the `src` prop or a context provider.
 * @example
 * <IconURLContext.Provider value="/icons/url">
 *   <Icon {...props} iconName="myIcon" />
 * </IconURLContext.Provider>
 */
export default memo(Icon);
